import Alpine from 'alpinejs';

window.Alpine = Alpine;

export default {
  init() {
    // JavaScript to be fired on all pages
    console.log('👋🏻 from common.ts');

    this.initCategoryAjax();
    this.initQuoteModal();

    Alpine.start();
  },

  initCategoryAjax() {
    // Prefetch-cache: URL → Promise<Document>
    const prefetchCache = new Map<string, Promise<Document>>();

    const fetchAndParse = (url: string): Promise<Document> => {
      if (!prefetchCache.has(url)) {
        const promise = fetch(url)
          .then((r) => {
            if (!r.ok) throw new Error('Network response was not ok');
            return r.text();
          })
          .then((html) => new DOMParser().parseFromString(html, 'text/html'))
          .catch((err) => {
            prefetchCache.delete(url);
            throw err;
          });
        prefetchCache.set(url, promise);
      }
      return prefetchCache.get(url)!;
    };

    // Prefetch vid hover/touch för snabbare navigering
    document.addEventListener('pointerenter', (e) => {
      const link = (e.target as Element)?.closest?.('.CategoryLink') as HTMLAnchorElement | null;
      if (!link?.href) return;
      // Mina sidor-navigationen ska inte prefetchas – annars GET:as t.ex.
      // logout-endpointen vid hover och kunden loggas ut oavsiktligt.
      if (link.closest('.MyAccountNavigation')) return;
      const url = link.href.split('#')[0];
      fetchAndParse(url);
    }, true);

    const loadCategory = async (url: string, scrollY: number, clickedLink: HTMLElement | null) => {
      try {
        document.body.classList.add('is-loading-category');

        // Omedelbar visuell feedback: markera klickad knapp som aktiv
        if (clickedLink) {
          const row = clickedLink.closest('.ContentSection');
          if (row) {
            row.querySelectorAll('.CategoryLink').forEach((el) => {
              el.classList.remove('is-active');
              el.classList.remove('is-parent');
            });
            row.classList.add('is-havingActiveCategoryLink');
          }
          clickedLink.classList.add('is-active');
        }

        const doc = await fetchAndParse(url);

        // 1. Uppdatera kategorinavigation
        const newNavs = doc.querySelectorAll('.CategoryNavigation');
        const currentNavs = document.querySelectorAll('.CategoryNavigation');
        currentNavs.forEach((nav, index) => {
          if (newNavs[index]) {
            nav.innerHTML = newNavs[index].innerHTML;
          }
        });

        // 2. Uppdatera produktgrid / innehåll
        const contentSelectors = ['.wp-block-query', '.products', 'main.wp-block-group', '#content'];
        for (const selector of contentSelectors) {
          const newElements = doc.querySelectorAll(selector);
          const currentElements = document.querySelectorAll(selector);
          if (newElements.length > 0 && currentElements.length > 0) {
            currentElements.forEach((el, index) => {
              if (newElements[index]) {
                el.innerHTML = newElements[index].innerHTML;
              }
            });
            break;
          }
        }

        // 3. URL, titel och sidrubriker
        window.history.pushState({}, doc.title, url);
        document.title = doc.title;
        const newTitles = doc.querySelectorAll('.wp-block-query-title');
        const currentTitles = document.querySelectorAll('.wp-block-query-title');
        currentTitles.forEach((title, index) => {
          if (newTitles[index]) {
            title.innerHTML = newTitles[index].innerHTML;
          }
        });

        // 4. Behåll scrollposition
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollY);
        });

        document.dispatchEvent(new CustomEvent('category-updated', { detail: { url } }));
      } catch (error) {
        console.error('AJAX Category Load failed:', error);
        if (error instanceof Error && error.message.includes('Network')) {
          window.location.href = url;
        }
      } finally {
        document.body.classList.remove('is-loading-category');
      }
    };

    // Triggas av inline script i wp_head som redan har stoppat navigering (preventDefault)
    document.addEventListener('brainforest-category-ajax', ((e: CustomEvent<{ url: string; scrollY: number; clickedLink?: HTMLElement }>) => {
      const { url, scrollY, clickedLink } = e.detail || {};
      if (url) {
        loadCategory(url, scrollY ?? window.scrollY, clickedLink ?? null);
      }
    }) as EventListener);

    window.addEventListener('popstate', () => {
      window.location.reload();
    });
  },

  initQuoteModal() {
    const open = () => {
      document.querySelector('.js-quote-modal')?.classList.add('is-active');
      document.body.classList.add('quote-modal-open');
    };

    const close = () => {
      document.querySelector('.js-quote-modal')?.classList.remove('is-active');
      document.body.classList.remove('quote-modal-open');
    };

    // Move hidden product-data inputs inside CF7 <form> so they get submitted
    const productData = document.querySelector('.quote-modal__product-data');
    const cf7Form = document.querySelector('.quote-modal__form form');
    if (productData && cf7Form) {
      Array.from(productData.children).forEach((input) => cf7Form.appendChild(input));
      productData.remove();
    }

    // Event delegation – works regardless of when elements are rendered
    document.addEventListener('click', (e) => {
      const target = e.target as Element;
      if (target.closest('.js-open-quote-modal')) {
        open();
      } else if (target.closest('.js-close-quote-modal')) {
        close();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && document.querySelector('.js-quote-modal.is-active')) {
        close();
      }
    });
  },

  finalize() {
    // JavaScript to be fired on all pages, after page specific JS is fired
  },
};
